import { z } from 'zod';
/**
 * One error shape for every route on every surface (architecture plan §19.1).
 *
 * `code` is the contract. Clients branch on it and never parse `message`, which exists
 * for logs and for a developer reading a response by hand. Every code below is a screen
 * state somewhere in docs/user-flows.md — that is why the list is short and closed, and
 * why adding one should feel like a decision.
 */
export const ERROR_CODES = [
    // --- transport / generic -------------------------------------------------
    'VALIDATION_FAILED',
    'NOT_FOUND',
    'CONFLICT',
    'RATE_LIMITED',
    'INTERNAL', // board 2.17 "Something went wrong"
    'APP_UPDATE_REQUIRED', // 1.1, the only screen allowed to dead-end
    // --- identity ------------------------------------------------------------
    'UNAUTHENTICATED',
    'ACCOUNT_UNVERIFIED', // 1.4 — an unverified account is not a usable account
    'ACCOUNT_SUSPENDED', // 1.1, 1.7
    'ADMIN_ACCESS_REMOVED', // 1.10
    'SESSION_IDLE_EXPIRED', // board 2.16 — D6
    'CREDENTIALS_INVALID', // 1.7, deliberately says nothing about which field
    // The fail-safe behind §6: a session was about to be issued to an admin without the
    // second factor having been satisfied. No route returns this on a normal journey —
    // seeing it means a new entry point forgot to ask, and it refused rather than
    // handing out the session.
    'MFA_REQUIRED',
    'TOO_MANY_ATTEMPTS', // 1.4, 1.7, 1.10
    'CODE_INCORRECT', // 1.4
    'CODE_EXPIRED', // 1.4
    'EMAIL_IN_USE', // 1.3, 7.2
    'PHONE_IN_USE', // 1.3, 7.2
    'INVITATION_EXPIRED', // 1.9
    'INVITATION_USED', // 1.9 — sends the provider to sign-in, not to an error
    'RESET_TOKEN_INVALID', // 1.12
    'ROLE_FORBIDDEN', // board 9.9 — names the role that *can*
    'PREFERENCE_LOCKED', // 7.4, board 8.13
    // --- marketplace and booking --------------------------------------------
    'CITY_NOT_COVERED', // 1.5
    // 2.1's "what must already be true": signed in, with a city set. Distinct from
    // CITY_NOT_COVERED, which is a city we know about and do not serve — this is a person
    // who has not chosen one, and the client's answer is 1.5 rather than an apology.
    'CITY_REQUIRED', // 2.1
    'PROVIDER_INACTIVE', // 4.3
    'SLOT_TAKEN', // 4.4, 4.5 — the exclusion constraint firing
    'HOLD_EXPIRED', // 4.5
    'HOLD_SERVICE_MISMATCH', // 4.4 — the kind cannot be swapped part-way
    'ALREADY_BOOKED', // board 4.14
    // Decision 5.36: one free introductory call per member per professional. Its own code
    // rather than a VALIDATION_FAILED reason, because the phone answers it with a
    // different screen (her call, or the professional's paid options), not a field error.
    'FREE_CALL_USED',
    'BOOKING_IN_PROGRESS', // 2.3 — changing city mid-booking
    // --- money ---------------------------------------------------------------
    'CARD_DECLINED', // 4.6, 6.3
    // --- learning and events -------------------------------------------------
    'CLASS_FULL', // 6.3
    // EVENT_FULL was here. D3 settled events as external-only (flows/events.md): the
    // platform lists them and hands off, so no event has a capacity to be full of.
    // Deleted rather than parked — a code whose feature is cancelled is not waiting
    // for a surface, it is waiting for nothing.
    'QUESTION_LOCKED', // 9.13 — already pushed, so answers stay meaningful
    // --- destructive actions (architecture plan §19.6) -----------------------
    'RESOLUTION_REQUIRED', // the mutation needs the decision the preview asked for
    'IMPACT_CHANGED', // something moved since the preview; read it again
    'DELETION_BLOCKED', // 7.6
    'LAST_ADMIN', // 9.18
];
export const ErrorCode = z.enum(ERROR_CODES);
export const ErrorBody = z.object({
    error: z.object({
        code: ErrorCode,
        message: z.string(),
        /** Machine-readable context: which field, which provider, which booking. */
        details: z.record(z.string(), z.unknown()).optional(),
        /** Threaded through logs and into any job the request spawns (plan §11). */
        request_id: z.string(),
    }),
});
/** HTTP status per code. One table, so no handler decides this on its own. */
export const ERROR_STATUS = {
    VALIDATION_FAILED: 422,
    NOT_FOUND: 404,
    CONFLICT: 409,
    RATE_LIMITED: 429,
    INTERNAL: 500,
    APP_UPDATE_REQUIRED: 426,
    UNAUTHENTICATED: 401,
    ACCOUNT_UNVERIFIED: 403,
    ACCOUNT_SUSPENDED: 403,
    ADMIN_ACCESS_REMOVED: 403,
    SESSION_IDLE_EXPIRED: 401,
    CREDENTIALS_INVALID: 401,
    MFA_REQUIRED: 403,
    TOO_MANY_ATTEMPTS: 429,
    CODE_INCORRECT: 422,
    CODE_EXPIRED: 410,
    EMAIL_IN_USE: 409,
    PHONE_IN_USE: 409,
    INVITATION_EXPIRED: 410,
    INVITATION_USED: 410,
    RESET_TOKEN_INVALID: 410,
    ROLE_FORBIDDEN: 403,
    PREFERENCE_LOCKED: 409,
    CITY_NOT_COVERED: 404,
    CITY_REQUIRED: 409,
    PROVIDER_INACTIVE: 409,
    SLOT_TAKEN: 409,
    HOLD_EXPIRED: 410,
    HOLD_SERVICE_MISMATCH: 409,
    ALREADY_BOOKED: 409,
    FREE_CALL_USED: 409,
    BOOKING_IN_PROGRESS: 409,
    CARD_DECLINED: 402,
    CLASS_FULL: 409,
    QUESTION_LOCKED: 409,
    RESOLUTION_REQUIRED: 409,
    IMPACT_CHANGED: 409,
    DELETION_BLOCKED: 409,
    LAST_ADMIN: 409,
};
//# sourceMappingURL=errors.js.map